/**
 * A creature's stat block under its game system's declared headings (issue 1591,
 * from issue 1575 decision 2: "the site's own monster pages use the same layout").
 *
 * ONE SOURCE. The layout is the system's `features.stat_block`, the same field
 * the tabletop reads: the committed snapshot carries it for a built-in system
 * (`scripts/mrcs/build-client-registry.js`) and `GET /api/registry` serves it as
 * `stat_block` for any other. The rules are the tabletop's, MIRRORED here
 * because the site's bundle cannot import `vtt/shared` (the frontend image's
 * build context is `./client`):
 *
 *   • `parseStatBlockLayout` and `groupStats` are
 *     `vtt/shared/src/layout/statBlock.ts`'s, line for line in behaviour: a
 *     layout refused whole reads as none; a heading's rows in the layout's
 *     order; a heading with none left out; what no heading places under
 *     "Other" (closed), or, with no headings at all, under "Statistics" (open).
 *   • `carriesStatBlock` is `vtt/server/src/entries.ts` `familiesFrom`'s test
 *     of which families a token may carry an entry of — the families whose
 *     stat block the tabletop draws under these headings.
 *
 * `vtt/server/src/site-stat-block.test.ts` runs both copies on the shipped
 * systems and fails when they disagree.
 *
 * Pure: no imports, so that test can load this file as it is.
 */

const LIMITS = { sections: 16, stats: 120, label: 40 };
const KEY_RE = /^(?:[a-z][a-z0-9-]{0,62}:)?[a-z0-9][a-z0-9._-]{0,99}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The catch-all heading's key: the stats no heading places. */
export const OTHER_SECTION = 'other';

/** The layout of a system that declares none: no headings. */
export const NO_STAT_BLOCK_LAYOUT = Object.freeze({ sections: [] });

/** A layout as stored, strictly (the tabletop's parser); null when it is not one. */
export function parseStatBlockLayout(v) {
  if (!isObj(v) || !Array.isArray(v.sections) || v.sections.length > LIMITS.sections) return null;
  const keys = new Set();
  const placed = new Set();
  const sections = [];
  for (const s of v.sections) {
    if (!isObj(s) || typeof s.key !== 'string' || !KEY_RE.test(s.key) || s.key === OTHER_SECTION || keys.has(s.key)) return null;
    const label = typeof s.label === 'string' ? s.label.trim() : '';
    // eslint-disable-next-line no-control-regex
    if (!label || label.length > LIMITS.label || /[\u0000-\u001f\u007f]/.test(label)) return null;
    if (s.open !== undefined && typeof s.open !== 'boolean') return null;
    if (!Array.isArray(s.stats) || s.stats.length > LIMITS.stats) return null;
    const stats = [];
    for (const k of s.stats) {
      if (typeof k !== 'string' || !KEY_RE.test(k) || placed.has(k)) return null;
      placed.add(k);
      stats.push(k);
    }
    if (placed.size > LIMITS.stats) return null;
    keys.add(s.key);
    sections.push({ key: s.key, label, open: s.open === true, stats });
  }
  return { sections };
}

/** The layout a system's `stat_block` reads as: the declared one, or none (as the tabletop's `statBlockLayoutFrom`). */
export function statBlockLayoutOf(raw) {
  return parseStatBlockLayout(raw) || NO_STAT_BLOCK_LAYOUT;
}

/** The catch-all heading for a layout: "Other" (closed) under headings, "Statistics" (open) with none. */
function catchAll(layout) {
  return layout.sections.length
    ? { key: OTHER_SECTION, label: 'Other', open: false }
    : { key: OTHER_SECTION, label: 'Statistics', open: true };
}

/** Rows (`{ key, label, value }`) under a layout's headings: the tabletop's `groupStats`. */
export function groupStats(rows, layout) {
  const byKey = new Map();
  for (const r of rows) if (r.key && !byKey.has(r.key)) byKey.set(r.key, r);
  const used = new Set();
  const out = [];
  for (const s of layout.sections) {
    const mine = s.stats.map((k) => byKey.get(k)).filter(Boolean);
    for (const r of mine) used.add(r);
    if (mine.length) out.push({ key: s.key, label: s.label, open: s.open, rows: mine });
  }
  const rest = rows.filter((r) => !used.has(r));
  if (rest.length) out.push({ ...catchAll(layout), rows: rest });
  return out;
}

/** The registers that make a family's entries something a token carries (the tabletop's `OFFER_REGISTERS`). */
const OFFER_REGISTERS = new Set(['hp', 'ac']);

/**
 * Does the tabletop draw this family's entries as a token's stat block? A raw
 * family document: one with a hit-point or armour-class attribute, or one
 * carrying a stat the system's combat rules keep as a resource (`resourceStats`).
 */
export function carriesStatBlock(doc, resourceStats = []) {
  const attrs = isObj(doc) && Array.isArray(doc.attributes) ? doc.attributes.filter(isObj) : [];
  const read = new Set(resourceStats);
  return attrs.some((a) => (typeof a.register === 'string' && OFFER_REGISTERS.has(a.register))
    || (typeof a.key === 'string' && read.has(a.key)));
}

/** The stats a system's combat rules keep as resources, from a registry `rules` (or `features`) object. */
export function resourceStatsOf(rules) {
  const combat = isObj(rules) && isObj(rules.combat) ? rules.combat : null;
  return combat && Array.isArray(combat.resources)
    ? combat.resources.filter(isObj).map((r) => r.stat).filter((k) => typeof k === 'string')
    : [];
}

/** Every attribute key a derived detail section shows. */
export function sectionKeys(section) {
  if (section.kind === 'heading') return section.sections.flatMap(sectionKeys);
  if (Array.isArray(section.members)) return section.members.map((m) => m.key);
  return section.attr ? [section.attr.key] : [];
}

/**
 * A family's derived detail sections (`familyRegistry.js` `deriveDetailSections`)
 * under the layout's headings, as `{ kind: 'heading', key, heading, open, sections }`.
 *
 * What a heading holds is what `groupStats` gives it, at the grain of the page's
 * sections: every row and group member goes under the heading that names it, or
 * under the catch-all; a whole section (prose, a link list, a table) moves under
 * a heading only when the layout names it, and otherwise keeps its place. A
 * group's display that is more than rows (a stat grid, a composed line) stays
 * whole wherever its members land together; a labelled-rows group is just rows
 * here, since the headings now do its grouping (under a system that declares no
 * headings it stays whole, heading and all). The headings sit where the
 * first of the stat block's sections did; the subtitle stays in the banner.
 */
export function layoutDetailSections(sections, layout) {
  const where = new Map();
  layout.sections.forEach((s) => s.stats.forEach((k, i) => where.set(k, { heading: s.key, at: i })));
  const pieces = new Map(); // heading key → [{ at, section }]
  const add = (heading, at, section) => {
    if (!pieces.has(heading)) pieces.set(heading, []);
    pieces.get(heading).push({ at, section });
  };
  let order = 0; // the stat block's own order, for the catch-all
  const kept = [];
  let insertAt = -1;

  for (const section of sections) {
    if (section.placement !== 'subtitle' && Array.isArray(section.members)) {
      if (insertAt < 0) insertAt = kept.length;
      // With no headings declared a labelled-rows group keeps its own heading: nothing else groups it.
      const asRows = section.kind === 'rows' || (layout.sections.length > 0 && section.style === 'rows');
      const parts = new Map();
      for (const m of section.members) {
        const w = where.get(m.key);
        const heading = w ? w.heading : OTHER_SECTION;
        const at = w ? w.at : (order += 1);
        if (asRows) {
          add(heading, at, { kind: 'rows', style: 'rows', heading: null, members: [m] });
        } else {
          if (!parts.has(heading)) parts.set(heading, { at, members: [] });
          parts.get(heading).members.push(m);
        }
      }
      for (const [heading, p] of parts) add(heading, p.at, { ...section, members: p.members });
      continue;
    }
    const w = section.placement !== 'subtitle' && section.attr ? where.get(section.attr.key) : null;
    if (w) {
      if (insertAt < 0) insertAt = kept.length;
      add(w.heading, w.at, section);
    } else {
      kept.push(section);
    }
  }
  if (insertAt < 0) return sections;

  const headingOf = (key, label, open) => {
    const sorted = (pieces.get(key) || []).slice().sort((a, b) => a.at - b.at).map((p) => p.section);
    // Neighbouring rows read as one run of rows, not a rule between each.
    const merged = [];
    for (const s of sorted) {
      const last = merged[merged.length - 1];
      if (s.kind === 'rows' && last && last.kind === 'rows') last.members = [...last.members, ...s.members];
      else merged.push(s.kind === 'rows' ? { ...s, members: [...s.members] } : s);
    }
    return merged.length ? { kind: 'heading', style: 'stat-heading', key, heading: label, open, sections: merged } : null;
  };
  const headings = [
    ...layout.sections.map((s) => headingOf(s.key, s.label, s.open)),
    (() => { const c = catchAll(layout); return headingOf(c.key, c.label, c.open); })(),
  ].filter(Boolean);
  return [...kept.slice(0, insertAt), ...headings, ...kept.slice(insertAt)];
}

/** Detail sections with each heading replaced by the sections it holds: every section the page draws, in order. */
export function flatDetailSections(sections) {
  return sections.flatMap((s) => (s.kind === 'heading' ? flatDetailSections(s.sections) : [s]));
}
